import { Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderFieldExtensions, ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { SettingsLayout, UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';
import { UnifiedThemeProvider } from '@backstage/theme';
import LightIcon from '@material-ui/icons/WbSunny';
import { accionaLightTheme } from './components/themes/accionaLightTheme';
import { CatalogUnprocessedEntitiesPage } from '@backstage/plugin-catalog-unprocessed-entities';
import { HomepageCompositionRoot } from '@backstage/plugin-home';
import { HomePage } from './components/home/HomePage';
import { configApiRef, microsoftAuthApiRef, useApi } from '@backstage/core-plugin-api';
import { RbacPage } from '@backstage-community/plugin-rbac';
import { catalogTranslations } from './components/translation/catalog/catalog';
import { catalogImportTranslations } from './components/translation/catalog-import/catalog-import';
import { catalogReactTranslations } from './components/translation/catalog-react/catalog-react';
import { coreComponentsTranslations } from './components/translation/core-components/core-components';
import { apiDocsTranslations } from './components/translation/api-docs/api-docs';
import { kubernetesTranslations } from './components/translation/kubernetes/kubernetes';
import { kubernetesReactTranslations } from './components/translation/kubernetes-react/kubernetes-react';
import { scaffolderTranslations } from './components/translation/scaffolder/scaffolder';
import { scaffolderReactTranslations } from './components/translation/scaffolder-react/scaffolder-react';
import { userSettingsTranslations } from './components/translation/user-settings/user-settings';
import { applicationPageTranslations } from './components/translation/application-page/application-page';
import { projectPageTranslations } from './components/translation/project-page/project-page';
import { ProjectsPage } from './components/catalog/ProjectPage/ProjectPage';
// import { columns } from './columns';
import { DynamicFormGeneratorCaseFieldExtension } from './components/scaffolder/DynamicFormGeneratorField/DynamicFormGeneratorField';
import { TemplateSelectorCaseFieldExtension } from './components/scaffolder/TemplateSelectorField/TemplateSelectorField';
import { TemplateSelectorCaseFieldExtensionCD } from './components/scaffolder/TemplateSelectorField/TemplateSelectorFieldCD';
import { TemplateSelectorCaseFieldExtensionIaC } from './components/scaffolder/TemplateSelectorField/TemplateSelectorFieldIaC';
import { ApplicationProjectsCaseFieldExtension } from './components/scaffolder/ApplicationProjectsField/ApplicationProjectsField';
import { entityPageTranslationRef, entityPageTranslations } from './components/translation/entity-page/entity-page';
import { RequestsPage } from './components/catalog/RequestPage';
import { ApplicationsPage } from './components/catalog/AplicationPage';
import { requestPageTranslations } from './components/translation/request-page/request-page';
import { projectTeamManagementTranslations } from './components/translation/project-team-management/project-team-management';
import './App.css';
import { componentTranslations } from './components/translation/component/component';
import {
  EntityKindPicker,
  EntityLifecyclePicker,
  EntityTypePicker,
} from '@backstage/plugin-catalog-react';
import { columns } from './columns';
import { ApplicationProjectsIaCCaseFieldExtension } from './components/scaffolder/ApplicationProjectsIaCField/ApplicationProjectsIaCField';
import { RepositoryTypeCloudFieldExtension } from './components/scaffolder/RepositoryTypeCloudField/RepositoryTypeCloudField';
import { AgentForgePage } from '@caipe/plugin-agent-forge';
import { CatalogKindGuard } from './components/catalog/CatalogKindGuard';
import { ProjectJPCaseFieldExtension } from './components/scaffolder/ProjectJPField/ProjectJPField';
import { DivisionCaseFieldExtension } from './components/scaffolder/DivisionField/DivisionField';
import { UserCaseFieldExtension } from './components/scaffolder/UserField/UserField';
import { Grid } from '@material-ui/core';
import logo from './components/Root/logo/logo_debox_clean.png';
import accionalogo from './components/Root/logo/logo-acciona.svg';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { ManageRoles } from './components/ManageRoles';
import { PlatformInsightsPage } from './components/PlatformInsights';
import { PermissionsAndDelegationsTab } from './components/catalog/SettingsPage';
import { ActivityLogPage, ActivityLogDashboardPage } from '@internal/plugin-activity-log';
import { AdminPage } from './components/AdminPage';
import { AdminAnnouncementsPage, AnnouncementsPage } from '@internal/plugin-announcements';
import { announcementsTranslations } from './components/translation/announcements/announcements';
import { DynamicFormGeneratorCaseFieldExtensionCD } from './components/scaffolder/DynamicFormGeneratorField/DynamicFormGeneratorFieldCD';
import { ApplicationComponentEnvCaseFieldExtension } from './components/scaffolder/ApplicationComponentEnvField/ApplicationComponentEnvField';
import { ExternalOnboardingPage } from '@internal/plugin-external-onboarding';
import { PlatformHostCaseFieldExtension } from './components/scaffolder/PlatformHostField/PlatformHostField';
import { DigitalHubEntityPickerFieldExtension } from './components/scaffolder/DigitalHubEntityPickerField/DigitalHubEntityPickerField';
import { OrgDataIaCCaseFieldExtension } from './components/scaffolder/OrgDataIaCField/OrgDataIaCField';
import { HostnameDomainCaseFieldExtension } from './components/scaffolder/HostnameDomainField/HostnameDomainField';
import { TooltipFieldExtension } from './components/scaffolder/TooltipField/TooltipField';
import { RemoteDocsFieldExtension } from './components/scaffolder/RemoteDocsField/RemoteDocsField';
import { scaffolderTooltipsTranslations } from './components/translation/scaffolder-tooltips/scaffolder-tooltips';
import { autom2plataformaTranslations } from './components/translation/autom2plataforma/autom2plataforma';

const app = createApp({
  themes: [
    // {
    //   id: 'accionaDarkTheme',
    //   title: 'Dark Theme',
    //   variant: 'dark',
    //   icon: <DarkIcon />,
    //   Provider: ({ children }) => (
    //     <UnifiedThemeProvider theme={accionaDarkTheme} children={children} />
    //   ),
    // },
    {
      id: 'accionaLightTheme',
      title: 'Light Theme',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={accionaLightTheme} children={children} />
      ),
    },
  ],
  apis,

  __experimentalTranslations: {
    availableLanguages: ['en', 'es'],
    resources: [catalogTranslations, catalogImportTranslations, catalogReactTranslations, coreComponentsTranslations, apiDocsTranslations, kubernetesTranslations, kubernetesReactTranslations, scaffolderTranslations, scaffolderReactTranslations, userSettingsTranslations, applicationPageTranslations, projectPageTranslations, entityPageTranslations, requestPageTranslations, projectTeamManagementTranslations, componentTranslations, announcementsTranslations, scaffolderTooltipsTranslations, autom2plataformaTranslations],
  },
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => {
      const configApi = useApi(configApiRef);
      const { t } = useTranslationRef(entityPageTranslationRef);
      if (configApi.getString('auth.environment') === 'development') {
        return (
          <SignInPage
            {...props}
            titleComponent={
              <Grid container direction='row' alignItems='flex-start' justifyContent="space-between">
                <Grid item>
                  <img width="255" height="160" src={logo} alt="Debox Logo" />
                </Grid>
                <Grid item>
                  <Grid container direction='column' alignItems='center' justifyContent="center">
                    <h2>{t('loginPage.title')}</h2>
                    <h3>{t('loginPage.subtitle')}</h3>
                  </Grid>
                </Grid>
                <Grid item>
                  <img width="225" height="150" src={accionalogo} alt="Acciona Logo" />
                </Grid>
              </Grid>}
            align='center'
            providers={[
              'guest'
            ]}
          />
        );
      }
      return (
        <SignInPage
          {...props}
          titleComponent={
            <Grid container direction='row' alignItems='flex-start' justifyContent="space-between">
              <Grid item>
                <img width="255" height="160" src={logo} alt="Debox Logo" />
              </Grid>
              <Grid item>
                <Grid container direction='column' alignItems='center' justifyContent="center">
                  <h2>{t('loginPage.title')}</h2>
                  <h3>{t('loginPage.subtitle')}</h3>
                </Grid>
              </Grid>
              <Grid item>
                <img width="225" height="150" src={accionalogo} alt="Acciona Logo" />
              </Grid>
            </Grid>}
          align='center'
          auto
          provider={
            {
              id: 'microsoft-auth-provider',
              title: "Bienvenido al Portal Interno para Desarrolladores (IDP) de Acciona",
              message: "Para acceder a la plataforma, inicie sesión con su correo corporativo",
              apiRef: microsoftAuthApiRef,
            }
          }
        />
      );
    }
  },
});

const routes = (
  <FlatRoutes>
    {/* <Route path="/" element={<Navigate to="catalog" />} /> */}
    <Route path="/" element={<Navigate to="home" />} />

    <Route path="/home" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>

    <Route path="/catalog" element={
      <CatalogKindGuard>
        <CatalogIndexPage
          actions={[]}
          columns={columns}
          filters={
            <>
              <EntityKindPicker allowedKinds={["Application", "Project", "Component", "API"]} />
              <EntityTypePicker />
              <EntityLifecyclePicker />
            </>
          }
          pagination={{ mode: 'offset', limit: 20 }} />
      </CatalogKindGuard>}
    />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route
      path="/catalog-unprocessed-entities"
      element={<CatalogUnprocessedEntitiesPage />}
    />
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage headerOptions={{ title: "Templates", subtitle: "Manage components and applications using standardized templates." }}
      groups={[
        {
          title: 'Aplicaciones',
          filter: entity =>
            entity?.spec?.type?.includes('aplicaciones') ?? false,
        },
        {
          title: 'Repositorios',
          filter: entity =>
            entity?.spec?.type?.includes('repositorios') ?? false,
        },
      ]}
    />}>
      <ScaffolderFieldExtensions>
        <DynamicFormGeneratorCaseFieldExtension />
        <DynamicFormGeneratorCaseFieldExtensionCD />
        <TemplateSelectorCaseFieldExtension />
        <TemplateSelectorCaseFieldExtensionCD />
        <TemplateSelectorCaseFieldExtensionIaC />
        <ApplicationProjectsCaseFieldExtension />
        <ApplicationProjectsIaCCaseFieldExtension />
        <RepositoryTypeCloudFieldExtension />
        <ProjectJPCaseFieldExtension />
        <DivisionCaseFieldExtension />
        <UserCaseFieldExtension />
        <ApplicationComponentEnvCaseFieldExtension />
        <PlatformHostCaseFieldExtension />
        <DigitalHubEntityPickerFieldExtension />
        <OrgDataIaCCaseFieldExtension />
        <HostnameDomainCaseFieldExtension />
        <TooltipFieldExtension />
        <RemoteDocsFieldExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route path="/applications" element={<ApplicationsPage />} />
    <Route path="/projects" element={<ProjectsPage />} />
    <Route path="/requests" element={<RequestsPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />}>
      <SettingsLayout.Route path="/permissions-delegations" title="Permissions & Delegations">
        <PermissionsAndDelegationsTab />
      </SettingsLayout.Route>
      {/* <SettingsLayout.Route path="/advanced" title="Advanced">
        <AdvancedSettings />
      </SettingsLayout.Route> */}
      {/* <SettingsLayout.Route path="/notifications" title="Notifications">
        <UserNotificationSettingsCard
          originNames={{ 'plugin:scaffolder': 'Scaffolder' }}
        />
      </SettingsLayout.Route> */}
    </Route>
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route path="/rbac" element={<RbacPage />} />;
    <Route path="/insights" element={<PlatformInsightsPage />} />
    <Route path="/agent-forge" element={<AgentForgePage />} />
    <Route path="/rbac" element={<RbacPage />} />
    <Route path="/manage-roles" element={<ManageRoles />} />
    <Route path="/activity-log" element={<ActivityLogPage />} />
    <Route path="/activity-log/dashboard" element={<ActivityLogDashboardPage />} />
    <Route path="/admin" element={<AdminPage />} />
    <Route path="/announcements" element={<AnnouncementsPage />} />
    <Route
      path="/announcements-admin"
      element={<AdminAnnouncementsPage />}
    />
    <Route path="/external-onboarding" element={<ExternalOnboardingPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
